/**
 * Fechas en la hora local de cada puerto. Mismas reglas que la maqueta:
 * - un instante guardado a medianoche UTC es una fecha sin hora: se lee en UTC para no correrla un día;
 * - las fechas de calendario (AAAA-MM-DD) ya están en la zona del puerto.
 */
export const ZONA_CHILE = "America/Santiago";
const DIA_MS = 86_400_000;

function zonaDe(iso: string, zona: string | null | undefined): string {
  return /T00:00:00(\.000)?Z$/.test(iso) ? "UTC" : (zona ?? ZONA_CHILE);
}

/** Día de calendario (AAAA-MM-DD) del instante en la zona dada. */
export function diaEn(iso: string, zona?: string | null): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: zonaDe(iso, zona),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
}

function numeroDeDia(dia: string): number {
  const [a, m, d] = dia.split("-").map(Number);
  return Date.UTC(a ?? 0, (m ?? 1) - 1, d ?? 1) / DIA_MS;
}

/** Día de calendario más n días. */
export function sumarDias(dia: string, n: number): string {
  return new Date((numeroDeDia(dia) + n) * DIA_MS).toISOString().slice(0, 10);
}

/** Días de calendario entre dos días (a − b). */
export function restarDias(a: string, b: string): number {
  return Math.round(numeroDeDia(a) - numeroDeDia(b));
}

/** Días de calendario entre dos instantes, cada uno en su zona. */
export function diasEntre(a: string, zonaA: string | null, b: string, zonaB: string | null): number {
  return restarDias(diaEn(a, zonaA), diaEn(b, zonaB));
}

type Opciones = Intl.DateTimeFormatOptions;

/** «12 oct 2026» en la zona del puerto; sin fecha, «—». */
export function fecha(iso: string | null | undefined, zona?: string | null, opciones: Opciones = {}): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-CL", {
    timeZone: zonaDe(iso, zona),
    day: "numeric",
    month: "short",
    year: "numeric",
    ...opciones,
  })
    .format(new Date(iso))
    .replace(/\./g, "");
}

/** «12 oct», sin año. */
export function fechaCorta(iso: string | null | undefined, zona?: string | null): string {
  return fecha(iso, zona, { year: undefined });
}

/** «12 oct 2026, 14:30» en hora de Chile. */
export function fechaHora(iso: string | null | undefined): string {
  return fecha(iso, ZONA_CHILE, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
}

/** Fecha de calendario «AAAA-MM-DD» con el formato de la interfaz. */
export function fechaDeDia(dia: string | null | undefined, opciones: Opciones = {}): string {
  return dia ? fecha(`${dia}T00:00:00Z`, "UTC", opciones) : "—";
}

/** Instante de un día en hora de Chile, para comparar «hoy». */
export function hoyEnChile(ahora: Date): string {
  return diaEn(ahora.toISOString(), ZONA_CHILE);
}
